// The tab's cache of Browse answers (sessionStorage `mg_browse:<request>`), so Back to a list is instant.
// B-161: every answer records whose it was; an answer for anyone but the session's user now is never shown.
// B-163: the cache used to live forever, so a Save, More like this, calendar add, check-in or group add never showed up in
// the order. Now an answer is fresh for FRESH_MS and only until the next taste signal (`markBrowseStale`); a stale answer
// still shows at once and the screen asks the server again quietly in the background (lib/useBrowse.ts). An answer older
// than MAX_MS is not shown at all.
//
// No imports on purpose (tests/owner_cache_test.ts runs it under Deno): the answer type is the caller's.

export const CACHE_PREFIX = 'mg_browse:'
/** When the last taste signal landed (sessionStorage): answers stored before it are stale. */
export const STALE_KEY = 'mg_browse_stale_at'
export const FRESH_MS = 3 * 60_000
export const MAX_MS = 30 * 60_000
/** The window event other modules fire after a taste signal (lib/groups.ts, lib/growth.ts can dispatch it too). */
export const STALE_EVENT = 'mg:browse-stale'

type Entry<T> = { v: 2; uid: string | null; at: number; res: T }
export type Hit<T> = { res: T; fresh: boolean }

const ss = () => { try { return globalThis.sessionStorage ?? null } catch { return null } }

function staleAt(): number {
  try { return Number(ss()?.getItem(STALE_KEY) ?? 0) || 0 } catch { return 0 }
}

/** The cached answer for this request if it is this user's and not too old; `fresh` = no need to ask the server again. */
export function readBrowse<T>(key: string, uid: string | null, now = Date.now()): Hit<T> | null {
  try {
    const raw = ss()?.getItem(CACHE_PREFIX + key)
    if (!raw) return null
    const e = JSON.parse(raw) as Entry<T> | null
    // an answer from before this rule (no owner on it), someone else's, or too old: never shown
    if (!e || e.v !== 2 || !('uid' in e) || e.uid !== uid || typeof e.at !== 'number' || now - e.at > MAX_MS || e.at > now + 60_000) return null
    return { res: e.res, fresh: now - e.at < FRESH_MS && e.at > staleAt() }
  } catch {
    return null
  }
}

/** Keep an answer for this request, owned by this user. `at` keeps an answer's age when a page is appended to it. */
export function writeBrowse<T>(key: string, uid: string | null, res: T, at = Date.now()) {
  try {
    ss()?.setItem(CACHE_PREFIX + key, JSON.stringify({ v: 2, uid, at, res } satisfies Entry<T>))
  } catch {
    /* a full session store only costs the instant back */
  }
}

/** The age stamp of a cached answer (to keep it when appending a page), or now. */
export function cachedAt(key: string): number {
  try {
    const e = JSON.parse(ss()?.getItem(CACHE_PREFIX + key) ?? 'null') as { at?: unknown } | null
    return typeof e?.at === 'number' ? e.at : Date.now()
  } catch {
    return Date.now()
  }
}

/** A taste signal landed (Save, unsave, More / Less like this, calendar add, check-in answer, group add): every cached
 *  answer is stale from now on, so the next Browse view asks again. Screens on view hear STALE_EVENT. */
export function markBrowseStale(now = Date.now()) {
  // the answer index.html fetched at page load predates this signal: never let it answer the refresh
  ;(globalThis as { __mgEarlyBrowse?: unknown }).__mgEarlyBrowse = undefined // = lib/earlyBrowse.ts dropEarlyBrowse()
  try { ss()?.setItem(STALE_KEY, String(now)) } catch { /* the time limit still refreshes it */ }
  try { globalThis.dispatchEvent?.(new Event(STALE_EVENT)) } catch { /* no window: tests */ }
}

/** Drop every cached answer (Not for me: the next load must not show it even for an instant). */
export function dropBrowseCache() {
  const s = ss()
  if (!s) return
  try {
    for (let i = s.length - 1; i >= 0; i--) {
      const k = s.key(i)
      if (k?.startsWith(CACHE_PREFIX)) s.removeItem(k)
    }
  } catch { /* nothing cached */ }
}
